// LivePreviewPane — sandboxed iframe showing the running workspace's web server
//
// Fetches the preview URL from workspace-service, then renders it in an <iframe>
// with a strict `sandbox` attribute.  Provides reload, open-in-tab, and share buttons.
// Subscribes to workspace socket events (file.saved, build.complete) for hot reload.

import { useState, useEffect, useRef, useCallback } from 'react';
import { ExternalLink, RefreshCw, Share2, AlertTriangle, Loader2 } from 'lucide-react';
import { io, type Socket } from 'socket.io-client';
import { cn } from '@/lib/utils';
import { get, post } from '@/lib/api';
import { getAuthToken } from '@/lib/auth';

interface PreviewMeta {
  url: string;
  workspaceId: string;
  status: string;
}

interface ShareResult {
  shareToken: string;
  url: string;
}

interface LivePreviewPaneProps {
  workspaceId: string;
  className?: string;
}

export default function LivePreviewPane({ workspaceId, className }: LivePreviewPaneProps) {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [shareUrl, setShareUrl]   = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError]         = useState<string | null>(null);
  const [copied, setCopied]       = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const reloadKey = useRef(0);
  const [reloadCount, setReloadCount] = useState(0);
  const socketRef = useRef<Socket | null>(null);

  // Subscribe to workspace socket for hot reload
  useEffect(() => {
    if (!workspaceId || !previewUrl) return;

    const token = getAuthToken();
    const socket = io({
      path: '/ws/terminal',
      query: { workspaceId },
      auth: { token: token ?? '' },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 2000,
    });
    socketRef.current = socket;

    // Hot-reload: refresh iframe when workspace signals a file save or build complete
    const triggerReload = () => {
      reloadKey.current++;
      setReloadCount(reloadKey.current);
    };
    socket.on('file.saved', triggerReload);
    socket.on('build.complete', triggerReload);

    return () => {
      socket.off('file.saved', triggerReload);
      socket.off('build.complete', triggerReload);
      socket.disconnect();
      socketRef.current = null;
    };
  }, [workspaceId, previewUrl]);

  // Fetch preview URL from workspace-service
  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError(null);

    get<PreviewMeta>(`/ide/workspaces/${workspaceId}/preview`)
      .then((meta) => {
        if (!cancelled) setPreviewUrl(meta.url);
      })
      .catch((err) => {
        if (!cancelled) setError(err?.message ?? 'Preview unavailable');
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => { cancelled = true; };
  }, [workspaceId]);

  const handleReload = useCallback(() => {
    if (!iframeRef.current || !previewUrl) return;
    reloadKey.current++;
    setReloadCount(reloadKey.current);
  }, [previewUrl]);

  const handleOpenTab = useCallback(() => {
    if (previewUrl) window.open(previewUrl, '_blank', 'noopener,noreferrer');
  }, [previewUrl]);

  const handleShare = useCallback(async () => {
    try {
      const res = await post<ShareResult>(`/ide/workspaces/${workspaceId}/share`, {});
      setShareUrl(res.url);
      await navigator.clipboard.writeText(res.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Share failed');
    }
  }, [workspaceId]);

  // ── Render ──────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div className={cn('flex h-full items-center justify-center bg-[#1e1e1e]', className)}>
        <Loader2 className="h-5 w-5 animate-spin text-[#858585]" />
      </div>
    );
  }

  if (error || !previewUrl) {
    return (
      <div className={cn('flex h-full flex-col items-center justify-center gap-3 bg-[#1e1e1e] p-6 text-center', className)}>
        <AlertTriangle className="h-8 w-8 text-yellow-500" />
        <p className="text-sm text-[#cccccc]">Preview unavailable</p>
        <p className="text-xs text-[#858585]">{error ?? 'The workspace is not running or has no web server on port 3000.'}</p>
        <button
          onClick={() => { setError(null); setIsLoading(true); setPreviewUrl(null); }}
          className="mt-2 rounded-md bg-[#2d2d2d] px-3 py-1.5 text-xs text-[#cccccc] hover:bg-[#3d3d3d] transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className={cn('flex h-full flex-col bg-[#1e1e1e]', className)}>
      {/* Toolbar */}
      <div className="flex h-9 shrink-0 items-center gap-1.5 border-b border-[#2d2d2d] px-2">
        {/* URL pill */}
        <div className="flex-1 rounded bg-[#2d2d2d] px-2 py-0.5 text-[11px] text-[#858585] font-mono truncate select-all">
          {previewUrl}
        </div>

        <button
          onClick={handleReload}
          title="Reload preview"
          className="p-1.5 text-[#858585] hover:text-white transition-colors"
        >
          <RefreshCw className="h-3.5 w-3.5" />
        </button>

        <button
          onClick={handleOpenTab}
          title="Open in new tab"
          className="p-1.5 text-[#858585] hover:text-white transition-colors"
        >
          <ExternalLink className="h-3.5 w-3.5" />
        </button>

        <button
          onClick={handleShare}
          title={copied ? 'Link copied!' : 'Share workspace'}
          className={cn(
            'p-1.5 transition-colors',
            copied ? 'text-green-400' : 'text-[#858585] hover:text-white',
          )}
        >
          <Share2 className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Share URL toast */}
      {shareUrl && copied && (
        <div className="shrink-0 bg-green-900/30 px-3 py-1.5 text-[11px] text-green-400 border-b border-green-800/40">
          Share link copied: <span className="font-mono">{shareUrl}</span>
        </div>
      )}

      {/* Sandboxed iframe — allow-scripts and allow-same-origin are needed for most web apps.
          allow-popups is intentionally omitted to prevent the preview from opening new windows. */}
      <iframe
        key={`${workspaceId}-${reloadCount}`}
        ref={iframeRef}
        src={previewUrl}
        title="Live preview"
        sandbox="allow-scripts allow-same-origin allow-forms"
        className="flex-1 w-full border-none bg-white"
        loading="lazy"
        referrerPolicy="no-referrer"
      />
    </div>
  );
}
